import { useEffect, useId, useMemo, useState } from "react";
import { type Dir, fmtHHMM, fmtRiders, rampColour, stationAbbr, stationLabel } from "../demand/demandMath";
import { useElementWidth } from "../demand/useElementWidth";
import {
  type BusAt,
  busesAt,
  busiestMinute,
  eventsBetween,
  firstDeparture,
  laneSpan,
  leftBehindAtStopUpTo,
  leftBehindPrefix,
  leftBehindUpTo,
  nextEventAfter,
  queueScaleMax,
  totalWaitingAt,
  waitingAt,
} from "./replayMath.ts";
import type { LeftBehindEvent, SimResult, SimStop } from "./types.ts";
import { useReplayClock } from "./useReplayClock.ts";
import "./dayReplay.css";

/** One simulated day to replay, named for its lane. */
export interface ReplayRun {
  label: string;
  result: SimResult;
}

interface Props {
  /** One or two days on the same stops. Two are drawn as stacked lanes sharing one clock. */
  runs: ReplayRun[];
  dirNames: Record<Dir, string>;
  /** The clock, once per whole simulated minute, for a cursor drawn elsewhere. */
  onTimeChange?: (min: number) => void;
  /** A request to jump the clock; a new seq is a new request. */
  seek?: { min: number; seq: number } | null;
}

/** Simulated minutes per real minute. ×60 is a minute of the day every second. */
const SPEEDS: Array<{ value: number; title: string }> = [
  { value: 30, title: "Half a minute of the day every second" },
  { value: 60, title: "A minute of the day every second" },
  { value: 120, title: "Two minutes of the day every second" },
  { value: 300, title: "Five minutes of the day every second" },
];

// Lane geometry. The route line runs across the middle; buses travel just
// above it (upward) and just below it (downward); queue bars grow away from
// the line over the bus track; stop names sit under everything.
const PAD_X = 36;
const TOP = 10;
const TRACK_H = 62;
const LABEL_H = 22;
const LINE_Y = TOP + TRACK_H;
const HEIGHT = TOP + 2 * TRACK_H + LABEL_H;
const BUS_W = 28;
const BUS_H = 14;
const BUS_OFF = 13;
const BAR_W = 10;
const BAR_GAP = 3;
const BAR_MAX = TRACK_H - 12;
/** A full bus is flagged on the bus for this long, and at the stop a little longer. */
const RECENT_BUS_MIN = 2;
const RECENT_STOP_MIN = 3;
const TIP_W = 260; // matches .dm-tip

type Hover = { kind: "bus"; id: string } | { kind: "stop"; stop: number } | null;

export default function DayReplay({ runs, dirNames, onTimeChange, seek }: Props) {
  const results = useMemo(() => runs.map((r) => r.result), [runs]);
  const [from, to] = useMemo(() => laneSpan(results), [results]);
  const start = useMemo(() => firstDeparture(results), [results]);
  const clock = useReplayClock({ from, to, initial: start, onMinute: onTimeChange });

  useEffect(() => {
    if (seek) clock.seek(seek.min);
    // Only a new request moves the clock, not a re-render with the old one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seek?.seq]);

  const queueMax = useMemo(() => queueScaleMax(results), [results]);
  const busiest = useMemo(() => busiestMinute(results), [results]);
  const prefixes = useMemo(() => results.map((r) => leftBehindPrefix(r.events)), [results]);
  const [wrapRef, width] = useElementWidth<HTMLDivElement>();
  const listId = useId();

  const stops = results[0]?.input.stops ?? [];
  const capacity = results[0]?.capacity ?? 1;

  const t = clock.time;
  let next: LeftBehindEvent | null = null;
  for (const r of results) {
    const e = nextEventAfter(r.events, t);
    if (e && (!next || e.timeMin < next.timeMin)) next = e;
  }

  // The status line changes every quarter hour of the day, not every frame:
  // a screen reader should hear a sentence now and then, not a torrent.
  const statusAt = Math.floor(t / 15) * 15;
  const status = useMemo(() => {
    const parts = runs.map((run, i) => {
      const waiting = fmtRiders(totalWaitingAt(run.result, statusAt));
      const buses = busesAt(run.result, statusAt, 0).length;
      const left = leftBehindUpTo(run.result.events, prefixes[i], statusAt);
      const text = `${waiting} riders waiting at stops · ${buses} bus${buses === 1 ? "" : "es"} running · ${
        left >= 0.5 ? `${fmtRiders(left)} turned away by full buses so far` : "nobody turned away so far"}`;
      return runs.length > 1 ? `${run.label}: ${text}` : text;
    });
    return `${fmtHHMM(statusAt)} — ${parts.join(" — ")}`;
  }, [runs, prefixes, statusAt]);

  const hours: number[] = [];
  for (let h = Math.ceil(from / 60) * 60; h <= to; h += 60) hours.push(h);

  if (runs.length === 0 || stops.length < 2) {
    return <div className="dm-empty">Nothing to replay yet.</div>;
  }

  return (
    <div className="sim-rp">
      <div className="sim-rp-controls">
        <button type="button" className="btn primary sim-rp-play" aria-pressed={clock.playing} onClick={clock.toggle}>
          {clock.playing ? "❚❚ Pause" : clock.atEnd ? "▶ Replay" : "▶ Play"}
        </button>
        <div className="sched-segmented sim-rp-speed" role="group" aria-label="Replay speed">
          {SPEEDS.map((s) => (
            <button key={s.value} type="button" className={clock.speed === s.value ? "active" : ""}
              title={s.title} aria-pressed={clock.speed === s.value} onClick={() => clock.setSpeed(s.value)}>
              ×{s.value}
            </button>
          ))}
        </div>
        <div className="sim-rp-slider">
          <input type="range" min={from} max={to} step={1} value={clock.minute} list={listId}
            aria-label="Time of day" aria-valuetext={fmtHHMM(clock.minute)}
            onChange={(e) => clock.seek(Number(e.target.value))} />
          <datalist id={listId}>
            {hours.map((h) => <option key={h} value={h} label={fmtHHMM(h)} />)}
          </datalist>
        </div>
        <span className="sim-rp-clock" aria-hidden>{fmtHHMM(clock.minute)}</span>
        <button type="button" className="btn" disabled={!next} title="Jump to the next time a full bus leaves riders at a stop"
          onClick={() => { if (next) clock.seek(next.timeMin); }}>
          Next full bus ▸
        </button>
        <button type="button" className="btn" disabled={busiest == null} title="Jump to the minute with the most riders waiting"
          onClick={() => { if (busiest != null) clock.seek(busiest); }}>
          Busiest moment
        </button>
      </div>

      <p className="sim-rp-status" role="status" aria-live="polite">{status}</p>

      <div className="sim-rp-lanes" ref={wrapRef}>
        {width > 0 && runs.map((run, i) => (
          <Lane key={run.label} run={run} t={t} width={width} stops={stops} dirNames={dirNames}
            queueMax={queueMax} capacity={capacity} prefix={prefixes[i]} single={runs.length === 1} />
        ))}
      </div>

      <div className="dm-legend sim-rp-legend">
        <span className="dm-legend-item">
          <span className="dm-swatch dm-swatch-upward" aria-hidden /> Above the line: buses {dirNames.upward} →
        </span>
        <span className="dm-legend-item">
          <span className="dm-swatch dm-swatch-downward" aria-hidden /> Below: ← buses {dirNames.downward}
        </span>
        <span className="dm-legend-item">
          <span className="dm-ramp" aria-hidden>
            {Array.from({ length: 11 }, (_, k) => <span key={k} style={{ background: rampColour(k / 10, "upward").fill }} />)}
          </span>
          Bus fill, empty to full ({capacity} riders); the figure is riders aboard
        </span>
        <span className="dm-legend-item">
          <svg width="10" height="14" aria-hidden><rect className="sim-rp-bar sim-rp-bar-upward" x="0" y="0" width="10" height="14" /></svg>
          Riders waiting at the stop (bar on a square-root scale to {fmtRiders(queueMax)}; the figure is exact)
        </span>
        <span className="dm-legend-item">
          <span className="sim-rp-flag-text" aria-hidden>▲</span> Riders a full bus has just turned away
        </span>
      </div>
    </div>
  );
}

interface LaneProps {
  run: ReplayRun;
  t: number;
  width: number;
  stops: SimStop[];
  dirNames: Record<Dir, string>;
  queueMax: number;
  capacity: number;
  prefix: number[];
  single: boolean;
}

/** The nose of a bus marker points the way it travels. */
function busPath(cx: number, cy: number, dir: Dir): string {
  const x0 = cx - BUS_W / 2;
  const x1 = cx + BUS_W / 2;
  const y0 = cy - BUS_H / 2;
  const y1 = cy + BUS_H / 2;
  const r = 3;
  const nose = 6;
  if (dir === "upward") {
    return `M${x0 + r},${y0} H${x1 - nose} L${x1},${cy} L${x1 - nose},${y1} H${x0 + r} ` +
      `Q${x0},${y1} ${x0},${y1 - r} V${y0 + r} Q${x0},${y0} ${x0 + r},${y0} Z`;
  }
  return `M${x1 - r},${y0} H${x0 + nose} L${x0},${cy} L${x0 + nose},${y1} H${x1 - r} ` +
    `Q${x1},${y1} ${x1},${y1 - r} V${y0 + r} Q${x1},${y0} ${x1 - r},${y0} Z`;
}

function Lane({ run, t, width, stops, dirNames, queueMax, capacity, prefix, single }: LaneProps) {
  const { result, label } = run;
  const n = stops.length;
  const [hover, setHover] = useState<Hover>(null);

  const span = Math.max(1, width - 2 * PAD_X);
  const xs = stops.map((_, i) => PAD_X + (n > 1 ? (i * span) / (n - 1) : span / 2));
  const perStop = n > 1 ? span / (n - 1) : span;
  const fullNames = perStop >= 96;

  const buses = busesAt(result, t, RECENT_BUS_MIN);
  const recent = eventsBetween(result.events, t - RECENT_STOP_MIN, t);
  const leftSoFar = leftBehindUpTo(result.events, prefix, t);
  const waitingNow = totalWaitingAt(result, t);

  const busX = (b: BusAt) => PAD_X + b.along * span;
  const busY = (dir: Dir) => (dir === "upward" ? LINE_Y - BUS_OFF : LINE_Y + BUS_OFF);
  // Queues run from a handful to over a thousand in one day. On a linear scale
  // everything but the worst stop would be a sliver, so the bar grows with the
  // square root; the figure beside it is the exact count.
  const barH = (riders: number) => (riders > 0 ? Math.min(BAR_MAX, Math.sqrt(riders / queueMax) * BAR_MAX) : 0);

  // The flag fades over its few minutes so a fresh one stands out from an old one.
  const fade = (timeMin: number, window: number) => Math.max(0.4, 1 - (0.6 * (t - timeMin)) / window);

  const hoveredBus = hover?.kind === "bus" ? buses.find((b) => b.trip.trip.id === hover.id) ?? null : null;
  const hoveredStop = hover?.kind === "stop" ? hover.stop : null;

  // The tooltip sits beside what it describes, to the right while there is room.
  const tipLeft = (x: number) => (x + 20 + TIP_W <= width ? x + 20 : Math.max(0, x - 20 - TIP_W));
  const tipTop = (y: number) => Math.max(0, Math.min(HEIGHT - 70, y - 28));

  const stopLabel = (s: SimStop) => (fullNames ? stationLabel(s.name) : stationAbbr(s.name));
  const upName = dirNames.upward;
  const downName = dirNames.downward;

  return (
    <div className="sim-rp-lane">
      <div className="sim-rp-lane-head">
        {!single && <strong>{label}</strong>}
        <span className="sim-rp-lane-live">
          {buses.length} bus{buses.length === 1 ? "" : "es"} on the road · {fmtRiders(waitingNow)} riders waiting
          {leftSoFar >= 0.5 && <> · <span className="dm-flag">{fmtRiders(leftSoFar)} turned away by full buses so far</span></>}
        </span>
      </div>

      <div className="sim-rp-stage">
        <svg width={width} height={HEIGHT} role="img"
          aria-label={`${label} at ${fmtHHMM(t)}: ${buses.length} buses on the road, ${fmtRiders(waitingNow)} riders waiting at stops`}>
          {/* stop columns, the line, its ticks and the way each track runs */}
          {xs.map((x, i) => (
            <line key={`g${i}`} className="sim-rp-grid" x1={x} x2={x} y1={TOP} y2={LINE_Y + TRACK_H} />
          ))}
          <line className="sim-rp-line" x1={xs[0]} x2={xs[n - 1]} y1={LINE_Y} y2={LINE_Y} />
          {xs.map((x, i) => (
            <circle key={`t${i}`} className="sim-rp-tick" cx={x} cy={LINE_Y} r={3.5} />
          ))}
          <text className="sim-rp-arrow" x={width - 4} y={busY("upward")} dy="0.35em" textAnchor="end">→</text>
          <text className="sim-rp-arrow" x={4} y={busY("downward")} dy="0.35em">←</text>

          {/* stop hit targets sit under the buses, so a bus at a stop wins the pointer */}
          {xs.map((x, i) => {
            const wUp = waitingAt(result, i, "upward", t);
            const wDown = waitingAt(result, i, "downward", t);
            return (
              <rect key={`h${i}`} className="dm-hit" x={x - 12} y={TOP} width={24} height={HEIGHT - TOP}
                tabIndex={0} role="img"
                aria-label={`${stationLabel(stops[i].name)}: ${fmtRiders(wUp)} waiting ${upName}, ${fmtRiders(wDown)} waiting ${downName}`}
                onPointerEnter={() => setHover({ kind: "stop", stop: i })}
                onPointerLeave={() => setHover((h) => (h?.kind === "stop" && h.stop === i ? null : h))}
                onFocus={() => setHover({ kind: "stop", stop: i })}
                onBlur={() => setHover(null)} />
            );
          })}

          {/* queues: a bar away from the line for each direction, with its figure */}
          {xs.map((x, i) => {
            const wUp = waitingAt(result, i, "upward", t);
            const wDown = waitingAt(result, i, "downward", t);
            const hUp = barH(wUp);
            const hDown = barH(wDown);
            return (
              <g key={`q${i}`}>
                {hUp > 0 && (
                  <rect className="sim-rp-bar sim-rp-bar-upward" x={x - BAR_W / 2} y={LINE_Y - BAR_GAP - hUp} width={BAR_W} height={hUp} />
                )}
                {wUp >= 10 && (
                  <text className="sim-rp-bar-text" x={x + BAR_W / 2 + 3} y={Math.min(LINE_Y - BAR_GAP - hUp + 4, LINE_Y - 24)}>{fmtRiders(wUp)}</text>
                )}
                {hDown > 0 && (
                  <rect className="sim-rp-bar sim-rp-bar-downward" x={x - BAR_W / 2} y={LINE_Y + BAR_GAP} width={BAR_W} height={hDown} />
                )}
                {wDown >= 10 && (
                  <text className="sim-rp-bar-text" x={x + BAR_W / 2 + 3} y={Math.max(LINE_Y + BAR_GAP + hDown, LINE_Y + 28)}>{fmtRiders(wDown)}</text>
                )}
              </g>
            );
          })}

          {/* a full bus has just left riders here */}
          {recent.map((e, k) => {
            const x = xs[e.stop];
            const h = barH(waitingAt(result, e.stop, e.dir, t));
            const y = e.dir === "upward" ? Math.min(LINE_Y - BAR_GAP - h - 5, LINE_Y - 30) : Math.max(LINE_Y + BAR_GAP + h + 12, LINE_Y + 38);
            return (
              <text key={`e${k}`} className="sim-rp-flag-text" x={x} y={y} textAnchor="middle" opacity={fade(e.timeMin, RECENT_STOP_MIN)}>
                ▲ {fmtRiders(e.riders)}
              </text>
            );
          })}

          {/* buses */}
          {buses.map((b) => {
            const x = busX(b);
            const y = busY(b.dir);
            const colour = rampColour(b.load / capacity, b.dir);
            return (
              <g key={b.trip.trip.id}>
                <path className="sim-rp-bus" d={busPath(x, y, b.dir)} fill={colour.fill} />
                <text className={`sim-rp-bus-text${colour.light ? " sim-rp-bus-text-light" : ""}`}
                  x={x + (b.dir === "upward" ? -2 : 2)} y={y} dy="0.35em" textAnchor="middle">
                  {Math.round(b.load)}
                </text>
                {b.leftBehind >= 0.5 && (
                  <text className="sim-rp-flag-text" x={x} y={b.dir === "upward" ? y - BUS_H / 2 - 3 : y + BUS_H / 2 + 11}
                    textAnchor="middle" opacity={fade(t - RECENT_BUS_MIN, RECENT_BUS_MIN)}>▲</text>
                )}
              </g>
            );
          })}
          {buses.map((b) => {
            const x = busX(b);
            const y = busY(b.dir);
            const id = b.trip.trip.id;
            return (
              <rect key={`bh${id}`} className="dm-hit" x={x - 16} y={y - 12} width={32} height={24} tabIndex={0} role="img"
                aria-label={`Trip ${fmtHHMM(b.trip.trip.calls[0].timeMin)} ${dirNames[b.dir]}: ${Math.round(b.load)} of ${capacity} aboard`}
                onPointerEnter={() => setHover({ kind: "bus", id })}
                onPointerLeave={() => setHover((h) => (h?.kind === "bus" && h.id === id ? null : h))}
                onFocus={() => setHover({ kind: "bus", id })}
                onBlur={() => setHover(null)} />
            );
          })}

          {/* stop names */}
          {xs.map((x, i) => {
            const anchor = fullNames && i === 0 ? "start" : fullNames && i === n - 1 ? "end" : "middle";
            const tx = anchor === "start" ? Math.max(2, x - 10) : anchor === "end" ? Math.min(width - 2, x + 10) : x;
            return (
              <text key={`l${i}`} className="sim-rp-stop-label" x={tx} y={HEIGHT - 6} textAnchor={anchor}>
                <title>{stops[i].name}</title>
                {stopLabel(stops[i])}
              </text>
            );
          })}
        </svg>

        {hoveredBus && (() => {
          const b = hoveredBus;
          const trip = b.trip.trip;
          const x = busX(b);
          const y = busY(b.dir);
          return (
            <div className="dm-tip" role="status" style={{ left: tipLeft(x), top: tipTop(y) }}>
              <div className="dm-tip-title">Trip {fmtHHMM(trip.calls[0].timeMin)} {dirNames[b.dir]}{trip.block > 0 ? ` · block ${trip.block}` : ""}</div>
              <div className="dm-tip-row">
                <span className={`dm-linekey dm-linekey-${b.dir}`} aria-hidden />
                <div>
                  <div><strong>{Math.round(b.load)}</strong> of {capacity} aboard</div>
                  <div className="dm-tip-detail">
                    {b.next ? `next stop ${stationLabel(stops[b.next.stop].name)} at ${fmtHHMM(b.next.timeMin)}` : "at the end of its trip"}
                    {b.leftBehind >= 0.5 && <> · <span className="dm-flag">▲ just turned {fmtRiders(b.leftBehind)} away</span></>}
                  </div>
                </div>
              </div>
              <div className="dm-tip-foot">
                Fullest point {fmtRiders(b.trip.maxLoad)} riders · carried {fmtRiders(b.trip.riders)} this trip
              </div>
            </div>
          );
        })()}

        {hoveredStop != null && (() => {
          const i = hoveredStop;
          const wUp = waitingAt(result, i, "upward", t);
          const wDown = waitingAt(result, i, "downward", t);
          const left = leftBehindAtStopUpTo(result.events, i, t);
          return (
            <div className="dm-tip" role="status" style={{ left: tipLeft(xs[i]), top: tipTop(LINE_Y) }}>
              <div className="dm-tip-title">{stops[i].name} · {fmtHHMM(t)}</div>
              {(["upward", "downward"] as Dir[]).map((dir) => {
                const w = dir === "upward" ? wUp : wDown;
                return (
                  <div className="dm-tip-row" key={dir}>
                    <span className={`dm-linekey dm-linekey-${dir}`} aria-hidden />
                    <div><strong>{fmtRiders(w)}</strong> waiting {dirNames[dir]}</div>
                  </div>
                );
              })}
              <div className="dm-tip-foot">
                {left >= 0.5 ? <span className="dm-flag">▲ turned away by full buses {fmtRiders(left)} times here so far</span> : "Nobody turned away here so far"}
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}
